/**
 * localStorage-backed draft, one per composer.
 *
 * Keyed by the composer's draft key — a channel `cid`, or a thread's `<cid>#reply:<id>`
 * — so the same screen showing two channels (an order's homeowner thread and its
 * internal notes) keeps two independent drafts without either surface naming a storage
 * key. The key FORMAT lives in `_libs/channel-draft-keys`, which is also what the
 * inbox's draft index reads.
 *
 * Only fully-uploaded attachments are persisted: an in-flight upload cannot be resumed
 * after a reload, and a restored draft holding a half-uploaded row disables the send
 * button forever.
 *
 * ## Two speeds
 *
 * The WRITE is debounced a second — nothing is waiting on it, and a `setItem` per
 * keystroke is the cost this hook exists to avoid. The INDEX behind the inbox's draft
 * mark is updated synchronously, because it is the only part a user can see, and a mark
 * that lands a second after the character reads as a bug.
 *
 * ## The pending write must not be dropped
 *
 * Both composer mount sites remount per draft key (`channel-view.tsx` keys on `cid`,
 * `channel-thread-view.tsx` on the compound reply key), so leaving a conversation
 * UNMOUNTS this hook. A pending timer is therefore FLUSHED on the way out; cancelling
 * it, which is what this did before, threw away everything typed in the last second
 * whenever someone typed and immediately switched conversations.
 *
 * The flush hangs off the same effect that seeds the mirror, so it always writes the key
 * it was scheduled for: on a key change React runs the old cleanup — closing over the
 * OLD `storageKey` and the OLD `mirrorRef.current` — before the new body re-seeds.
 *
 * The mirror is seeded in the `useRef` INITIALIZER, not in that effect:
 * `use-chat-composer-form.ts` reads `draft.read()` while computing its default values,
 * during the first render, before any effect has run. Moving the seed would give every
 * composer an empty box on mount.
 */

import { useEffect, useMemo, useRef } from "react";
import type { AudioAttachment } from "@/features/shared/audio-recorder";
import type {
  ChatComposerDraft,
  ChatComposerValues,
} from "@/features/shared/chat-composer";
import type { AttachmentWithStatus } from "@/features/shared/upload";
import {
  channelDraftStorageKey,
  isChannelDraftStorageKey,
} from "../_libs/channel-draft-keys";
import {
  resyncChannelDraftIndex,
  setChannelDraftPresence,
} from "./use-channel-draft-index";

const DRAFT_AUTO_SAVE_DELAY_MS = 1000;

type ChannelDraftData = {
  content: string;
  attachments: AttachmentWithStatus[];
  audios: AudioAttachment[];
};

const EMPTY_DRAFT: ChannelDraftData = {
  content: "",
  attachments: [],
  audios: [],
};

/**
 * Drops every stored draft.
 *
 * For the crash screen's "Reload page": a draft holding a value the composer cannot
 * restore is one of the few things a reload alone will not fix, so the error boundary
 * clears them all rather than guessing which channel broke.
 */
export function clearChannelDrafts(): void {
  try {
    const keys = Object.keys(localStorage).filter(isChannelDraftStorageKey);
    for (const key of keys) localStorage.removeItem(key);
  } catch (error) {
    console.error("Failed to clear channel drafts:", error);
  }
  // Outside the try: the index is in memory and cannot throw, and the rows must stop
  // marking drafts that are gone even if the removal above failed part way through.
  resyncChannelDraftIndex();
}

function readDraft(storageKey: string | null): ChannelDraftData {
  if (!storageKey) return EMPTY_DRAFT;
  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) return EMPTY_DRAFT;

    const draft = JSON.parse(stored) as Partial<ChannelDraftData>;
    return {
      content: draft.content ?? "",
      attachments: draft.attachments ?? [],
      audios: draft.audios ?? [],
    };
  } catch {
    return EMPTY_DRAFT;
  }
}

function isEmptyDraft({ content, attachments, audios }: ChannelDraftData) {
  return (
    content.trim() === "" && attachments.length === 0 && audios.length === 0
  );
}

/**
 * An empty draft is REMOVED rather than stored, so a key's existence is itself the
 * answer to "does this conversation hold unsent text?" — which is what the inbox index
 * is rebuilt from on load, and why this branch must never become a write of
 * `EMPTY_DRAFT`.
 */
function writeDraft(storageKey: string, draft: ChannelDraftData): void {
  try {
    if (isEmptyDraft(draft)) {
      localStorage.removeItem(storageKey);
      return;
    }
    localStorage.setItem(storageKey, JSON.stringify(draft));
  } catch (error) {
    console.error("Failed to save channel draft:", error);
  }
}

export function useChannelDraft(draftKey: string): ChatComposerDraft {
  const storageKey = draftKey ? channelDraftStorageKey(draftKey) : null;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mirrorRef = useRef<ChannelDraftData>(readDraft(storageKey));

  useEffect(() => {
    // A key change must reload the mirror, or the second channel opens holding the
    // first channel's text. Both call sites remount instead today, so in practice this
    // runs once per mount — it stays because the hook must not depend on that.
    mirrorRef.current = readDraft(storageKey);

    const flushPendingSave = () => {
      if (!timerRef.current || !storageKey) return;
      clearTimeout(timerRef.current);
      timerRef.current = null;
      writeDraft(storageKey, mirrorRef.current);
    };

    // A reload or a tab close inside the debounce window would otherwise lose the last
    // second of typing — the same loss the unmount flush below fixes, by the other exit.
    // `pagehide` covers a bfcache freeze too, which `beforeunload` does not reliably.
    window.addEventListener("pagehide", flushPendingSave);

    return () => {
      window.removeEventListener("pagehide", flushPendingSave);
      flushPendingSave();
    };
  }, [storageKey]);

  return useMemo<ChatComposerDraft>(() => {
    const cancelPendingSave = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
    };

    const scheduleSave = () => {
      if (!storageKey) return;
      // Synchronous, so the inbox's mark tracks the keystroke. The WRITE waits.
      setChannelDraftPresence(storageKey, !isEmptyDraft(mirrorRef.current));
      cancelPendingSave();
      timerRef.current = setTimeout(() => {
        // Cleared BEFORE the write: a fired timer is not pending, and an unmount that
        // still saw a handle here would write the same value a second time.
        timerRef.current = null;
        writeDraft(storageKey, mirrorRef.current);
      }, DRAFT_AUTO_SAVE_DELAY_MS);
    };

    return {
      key: draftKey,
      read: () => mirrorRef.current satisfies Partial<ChatComposerValues>,
      setContent: (content) => {
        mirrorRef.current = { ...mirrorRef.current, content };
        scheduleSave();
      },
      setAttachments: (attachments) => {
        mirrorRef.current = {
          ...mirrorRef.current,
          attachments: attachments.filter((a) => a.status === "done"),
        };
        scheduleSave();
      },
      setAudios: (audios) => {
        mirrorRef.current = { ...mirrorRef.current, audios };
        scheduleSave();
      },
      clear: () => {
        cancelPendingSave();
        mirrorRef.current = EMPTY_DRAFT;
        if (!storageKey) return;
        setChannelDraftPresence(storageKey, false);
        try {
          localStorage.removeItem(storageKey);
        } catch (error) {
          console.error("Failed to clear channel draft:", error);
        }
      },
    };
  }, [storageKey, draftKey]);
}
